/**
 * Centralised SweetAlert2 wrapper (aligned with fortify_user_portal).
 */

import Swal from "sweetalert2";
import { uploadingFile, wait } from "lib/sweetAlertHtmls";

const customClass = {
  popup: "swal_popup",
  title: "swal_title",
  htmlContainer: "swal_text",
  actions: "swal_actions",
};

const DEFAULT_CONFIRM_TEXT = "OK";
const DEFAULT_REJECT_TEXT = "";
const SUCCESS_TIMER = 4000;

const normalizeArgs = (title, text) => {
  if (text === undefined && typeof title === "string") {
    return { title: undefined, text: title };
  }
  return { title, text };
};

const buildAlertConfig = ({
  icon,
  title,
  defaultTitle,
  text,
  html,
  confirmText = DEFAULT_CONFIRM_TEXT,
  rejectButtonText = DEFAULT_REJECT_TEXT,
  timer,
  timerProgressBar = false,
  showCloseButton = false,
}) => {
  const normalizedRejectButtonText =
    typeof rejectButtonText === "string" ? rejectButtonText.trim() : "";

  return {
    icon,
    title: title ?? defaultTitle,
    text,
    html,
    timer,
    timerProgressBar,
    confirmButtonText: confirmText,
    showCancelButton: !!normalizedRejectButtonText,
    cancelButtonText: normalizedRejectButtonText,
    showDenyButton: false,
    denyButtonText: "",
    allowOutsideClick: false,
    showCloseButton,
    customClass: { ...customClass },
    buttonsStyling: true,
    confirmButtonColor: "#0f4c81",
    cancelButtonColor: "#f1f5f9",
  };
};

/** Native `<dialog>` uses the top layer and sits above Swal unless closed first. */
function closeOpenNativeDialogs() {
  document.querySelectorAll("dialog[open]").forEach((dialog) => {
    dialog.close();
  });
}

/** Avoid stacked modals where button handlers stop working. */
const fireSwal = (options) => {
  if (Swal.isVisible()) {
    Swal.close();
  }
  closeOpenNativeDialogs();
  return Swal.fire({
    ...options,
    target: document.body,
  });
};

const fireProgressAlert = ({ htmlContent, titlePrefix }) => {
  let intervalId;
  return fireSwal({
    html: htmlContent,
    allowOutsideClick: false,
    showConfirmButton: false,
    backdrop: true,
    customClass: { ...customClass },
    buttonsStyling: true,
    didOpen: () => {
      const titleElement = document.getElementById("uploading-title");
      const progressBar = document.querySelector(".progress-bar");
      if (!titleElement || !progressBar) return;

      let progress = 0;
      let dotCount = 1;
      intervalId = window.setInterval(() => {
        dotCount = (dotCount % 3) + 1;
        titleElement.textContent = `${titlePrefix}${" .".repeat(dotCount)}`;
        if (progress < 100) {
          progress += 10;
          progressBar.style.width = `${progress}%`;
          return;
        }
        window.clearInterval(intervalId);
      }, 500);
    },
    willClose: () => {
      if (intervalId) window.clearInterval(intervalId);
    },
  });
};

const swtAlert = {
  success: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return fireSwal(
      buildAlertConfig({
        icon: "success",
        title: args.title,
        defaultTitle: "Success",
        text: args.text,
        confirmText,
        rejectButtonText,
        timer: SUCCESS_TIMER,
        timerProgressBar: true,
        showCloseButton: true,
      })
    );
  },

  error: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return fireSwal(
      buildAlertConfig({
        icon: "error",
        title: args.title,
        defaultTitle: "Error",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  info: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return fireSwal(
      buildAlertConfig({
        icon: "info",
        title: args.title,
        defaultTitle: "Info",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  warning: (
    title,
    text,
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) => {
    const args = normalizeArgs(title, text);
    return fireSwal(
      buildAlertConfig({
        icon: "warning",
        title: args.title,
        defaultTitle: "Warning",
        text: args.text,
        confirmText,
        rejectButtonText,
      })
    );
  },

  warningHtml: (
    title = "Warning",
    html = "",
    confirmText = DEFAULT_CONFIRM_TEXT,
    rejectButtonText = DEFAULT_REJECT_TEXT
  ) =>
    fireSwal(
      buildAlertConfig({
        icon: "warning",
        title,
        defaultTitle: "Warning",
        html,
        confirmText,
        rejectButtonText,
      })
    ),

  loader: (title = "Please wait...", text = "") =>
    fireSwal({
      title,
      text,
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: { ...customClass },
      buttonsStyling: true,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    }),

  uploading: () =>
    fireProgressAlert({
      htmlContent: uploadingFile(),
      titlePrefix: "Uploading",
    }),

  waiting: () =>
    fireProgressAlert({
      htmlContent: wait(),
      titlePrefix: "Please wait",
    }),

  close: () => Swal.close(),
};

export default swtAlert;
